import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { Checkbox } from 'antd'

import { countriesForPicker, subdivisionOptions, subdivisionTermKey } from '@vacationtracker/shared/data/countries'
import { filterCountryOptions } from '@vacationtracker/shared/functions/filter-countries'
import { HOURS_MAX, HOURS_MIN, HOURS_STEP } from '@vacationtracker/shared/functions/work-week'
import { LocaleEnum } from '@vacationtracker/shared/types/i18n'
import { ExpandableCard } from '../../../../../../components/ui/expandable-card'
import { publicHolidaysForPlace } from '../../../../../../util/location-facts'
import { DaysOfWeekField } from '../../../../../../design-system/DaysOfWeekField'
import { SelectField } from '../../../../../../design-system/SelectField'
import { SegmentedToggle } from '../../../../../../design-system/SegmentedToggle'
import { Stack } from '../../../../../../design-system/Stack'
import { Text } from '../../../../../../design-system/Text'
import { TextField } from '../../../../../../design-system/TextField'
import { IEditorActions } from '../../../../../../components/inline-edit/EditableRow'
import IntlMessages from '../../../../../../util/IntlMessages'
import { useDayNames } from '../../../../../../hooks/use-day-names'
import { numberOfDaysInMonth } from '../../../../../../util/number-of-days-in-month'
import { months } from '../../../../../../util/months'
import { IWorkWeekChange } from './location-event-payloads'

/**
 * The settings rows commit on an explicit Save, so every editor reports its draft upward rather
 * than committing on change the way the users table's cells do.
 */

export const TextRowEditor = ({
  value,
  maxLength,
  required = false,
  actions,
  ariaLabel,
}: {
  value: string
  maxLength?: number
  /** Blocks Save while the field is blank, without making a cleared field read as unchanged. */
  required?: boolean
  actions: IEditorActions<string>
  /** Names the inline control for assistive tech - TextField requires it on labelless uses. */
  ariaLabel: string
}): React.ReactElement => {
  const [draft, setDraft] = useState(value)

  return (
    <TextField
      autoFocus
      ariaLabel={ariaLabel}
      maxLength={maxLength}
      value={draft}
      status={required && !draft.trim() ? 'danger' : undefined}
      onChange={(next) => {
        const text = String(next ?? '')
        setDraft(text)
        actions.setDraft(text)
        actions.setDirty(text !== value)
        if (required) {
          actions.setValid(Boolean(text.trim()))
        }
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          actions.cancel()
        }
      }}
    />
  )
}

export const SelectRowEditor = <TValue extends string | number>({
  value,
  options,
  actions,
  width,
  ariaLabel,
}: {
  value: TValue
  options: { value: TValue; label: string }[]
  actions: IEditorActions<TValue>
  width?: number
  /** Names the inline control for assistive tech - SelectField requires it on labelless uses. */
  ariaLabel: string
}): React.ReactElement => {
  const [draft, setDraft] = useState(value)

  return (
    <SelectField
      autoFocus
      ariaLabel={ariaLabel}
      value={draft}
      options={options}
      style={{ width: width ?? '100%', maxWidth: 400 }}
      onChange={(next: TValue) => {
        setDraft(next)
        actions.setDraft(next)
        actions.setDirty(next !== value)
      }}
    />
  )
}

/** For a field with two or three choices, where a dropdown would hide one behind a click. */
export const ToggleRowEditor = ({
  value,
  options,
  actions,
}: {
  value: string
  options: { value: string; label: string }[]
  actions: IEditorActions<string>
}): React.ReactElement => {
  const [draft, setDraft] = useState(value)

  return (
    <SegmentedToggle
      value={draft}
      options={options}
      onChange={(next) => {
        const picked = next as string
        setDraft(picked)
        actions.setDraft(picked)
        actions.setDirty(picked !== value)
      }}
    />
  )
}

export interface IPlaceValue {
  countryCode: string | null
  subdivisionCode: string | null
  /** Ticked when the admin wants this country's public holidays brought in on save. */
  importHolidays: boolean
}

export const PlaceEditor = ({ value, actions, locale }: { value: IPlaceValue; actions: IEditorActions<IPlaceValue>; locale: LocaleEnum }): React.ReactElement => {
  const { formatMessage } = useIntl()
  const [country, setCountry] = useState(value.countryCode)
  const [subdivision, setSubdivision] = useState(value.subdivisionCode)
  const [importHolidays, setImportHolidays] = useState(false)

  const subdivisions = country ? subdivisionOptions(country) : []
  const placeChanged = country !== value.countryCode || subdivision !== value.subdivisionCode
  // Only while the place is actually changing: re-importing the holidays a location already has is
  // not something to offer for opening the row. Some countries have no data in the
  // bundled library, so the helper - the same one the create form uses, so the count offered and
  // the set imported cannot disagree - also decides whether there is anything to import at all.
  const offer = placeChanged && country ? publicHolidaysForPlace({ countryCode: country, subdivisionCode: subdivision }, locale) : undefined

  const report = (next: IPlaceValue) => {
    const changed = next.countryCode !== value.countryCode || next.subdivisionCode !== value.subdivisionCode
    // Reverting to the stored country hides the offer, so drop the tick with it rather than
    // importing on a save that changes nothing.
    actions.setDraft({ ...next, importHolidays: changed && next.importHolidays })
    actions.setDirty(changed)
  }

  return (
    <Stack gap="sm" align="flex-start">
      {/* The region qualifies the country, so the two read across rather than down. Wrapping keeps
          them stacked on a narrow screen instead of squeezing both. */}
      <Stack direction="horizontal" gap="xs" align="center" wrap>
        <SelectField
          autoFocus
          ariaLabel={formatMessage({ id: 'locationPlace.country.placeholder' })}
          clearable
          value={country ?? undefined}
          placeholder={formatMessage({ id: 'locationPlace.country.placeholder' })}
          options={countriesForPicker().map((item) => ({ value: item.iso, label: item.name }))}
          filterOption={filterCountryOptions}
          style={{ width: 260, maxWidth: '100%' }}
          onChange={(iso?: string) => {
            const next = iso ?? null
            setCountry(next)
            // The old subdivision belongs to the old country; keeping it would only produce a rejection.
            setSubdivision(null)
            report({ countryCode: next, subdivisionCode: null, importHolidays })
          }}
        />
        {subdivisions.length > 0 && (
          <SelectField
            ariaLabel={formatMessage({ id: subdivisionTermKey(country as string) })}
            clearable
            value={subdivision ?? undefined}
            placeholder={formatMessage({ id: subdivisionTermKey(country as string) })}
            options={subdivisions}
            style={{ width: 220, maxWidth: '100%' }}
            onChange={(iso?: string) => {
              const next = iso ?? null
              setSubdivision(next)
              report({ countryCode: country, subdivisionCode: next, importHolidays })
            }}
          />
        )}
      </Stack>
      {offer && (
        <ExpandableCard
          active={importHolidays}
          control={<Checkbox checked={importHolidays} />}
          title={<IntlMessages id="locationGeneral.reimportHolidays" values={{ count: offer.holidays.length, place: offer.place }} />}
          description={<IntlMessages id="locationPlace.holidays.replacesAll" />}
          onClick={() => {
            const next = !importHolidays
            setImportHolidays(next)
            report({ countryCode: country, subdivisionCode: subdivision, importHolidays: next })
          }}
        />
      )}
    </Stack>
  )
}

export interface ILeaveYearValue {
  resetQuotas: string
  yearStartMonth: number
  yearStartDay: number
}

export const LeaveYearEditor = ({ value, actions }: { value: ILeaveYearValue; actions: IEditorActions<ILeaveYearValue> }): React.ReactElement => {
  const { formatMessage } = useIntl()
  const [draft, setDraft] = useState(value)

  const report = (next: ILeaveYearValue) => {
    setDraft(next)
    actions.setDraft(next)
    actions.setDirty(next.resetQuotas !== value.resetQuotas || next.yearStartMonth !== value.yearStartMonth || next.yearStartDay !== value.yearStartDay)
  }

  const changed = draft.resetQuotas !== value.resetQuotas || draft.yearStartMonth !== value.yearStartMonth || draft.yearStartDay !== value.yearStartDay

  return (
    <Stack gap="sm" align="flex-start">
      {/* The reset basis and the year's start date read as one sentence, so they share a line. */}
      <Stack direction="horizontal" align="center" gap="sm" wrap>
        <Text tone="secondary" size="sm">
          <IntlMessages id="locationGeneral.quotaResetBasedOn" />
        </Text>
        <SegmentedToggle
          value={draft.resetQuotas}
          options={[
            { value: 'FISCAL_YEAR', label: formatMessage({ id: 'components.locationForm.fiscalYear' }) },
            { value: 'USER_YEAR', label: formatMessage({ id: 'users.employmentDate' }) },
          ]}
          onChange={(next) => report({ ...draft, resetQuotas: next as string })}
        />
        {draft.resetQuotas === 'FISCAL_YEAR' && (
          // The date sits under its own label rather than trailing it, so the month and day stay
          // together on one line instead of being split by whatever width is left.
          <Stack gap="xxs">
            <Text tone="secondary" size="sm">
              <IntlMessages id="locationGeneral.yearStartsOn" />
            </Text>
            <Stack direction="horizontal" align="center" gap="sm">
              <SelectField
                ariaLabel={formatMessage({ id: 'app.month' })}
                searchable={false}
                value={draft.yearStartMonth}
                options={months.map((item) => ({ value: item.value, label: formatMessage({ id: item.label }) }))}
                style={{ width: 150 }}
                onChange={(month: number) => {
                  // 31 January is fine, 31 February is not - clamp rather than send a date that does not exist.
                  const day = draft.yearStartDay > numberOfDaysInMonth(month) ? 1 : draft.yearStartDay
                  report({ ...draft, yearStartMonth: month, yearStartDay: day })
                }}
              />
              <SelectField
                ariaLabel={formatMessage({ id: 'app.day' })}
                searchable={false}
                value={draft.yearStartDay}
                options={Array.from({ length: numberOfDaysInMonth(draft.yearStartMonth) }, (_, index) => ({
                  value: index + 1,
                  label: String(index + 1),
                }))}
                style={{ width: 90 }}
                onChange={(day: number) => report({ ...draft, yearStartDay: day })}
              />
            </Stack>
          </Stack>
        )}
      </Stack>
      {changed && (
        <Text tone="danger" size="sm">
          <IntlMessages id="components.locationForm.warningInitialRollover" />
        </Text>
      )}
    </Stack>
  )
}

export interface IWorkWeekValue {
  workWeek: number[]
  hoursPerDay?: number | null
}

/**
 * Both work-week rows post the same LOCATION_WORK_WEEK_UPDATED event, so each sends the half it does
 * not edit unchanged. They share this control because a custom work week carries its own hours too:
 * either row can decide whether people who set their own are overwritten.
 */
const ApplyToRow = ({ value, customCount, onChange }: { value: boolean; customCount: number; onChange: (next: boolean) => void }): React.ReactElement => {
  const { formatMessage } = useIntl()

  // With nobody to spare, the choice has no second outcome - so say so instead of offering it.
  if (customCount === 0) {
    return (
      <Text tone="tertiary" size="sm">
        <IntlMessages id="locationGeneral.everyoneFollows" />
      </Text>
    )
  }

  return (
    <Stack gap="xxs">
      <Stack direction="horizontal" align="center" gap="xs" wrap>
        <Text tone="secondary" size="sm">
          <IntlMessages id="locationGeneral.applyTo" />
        </Text>
        <SegmentedToggle
          value={value}
          options={[
            { value: true, label: formatMessage({ id: 'locationGeneral.applyToEveryone' }) },
            { value: false, label: formatMessage({ id: 'locationGeneral.applyToFollowing' }) },
          ]}
          onChange={(next) => onChange(next as boolean)}
        />
      </Stack>
      {/* Spells out what the pick costs the people who set their own week. */}
      <Text tone="tertiary" size="sm">
        <IntlMessages id={value ? 'locationGeneral.applyToEveryoneHint' : 'locationGeneral.applyToFollowingHint'} values={{ count: customCount }} />
      </Text>
    </Stack>
  )
}

export const WorkWeekEditor = ({
  value,
  firstDayOfWeek,
  customCount,
  actions,
}: {
  value: IWorkWeekValue
  firstDayOfWeek: number
  customCount: number
  actions: IEditorActions<IWorkWeekChange>
}): React.ReactElement => {
  const { formatMessage } = useIntl()
  const dayNames = useDayNames()
  const [days, setDays] = useState<number[]>([...value.workWeek])
  const [applyToEveryone, setApplyToEveryone] = useState(false)

  // Dirty means "differs from what is stored", not "is valid" - conflating them let deselecting
  // every day read as clean, so the navigation guard concluded there was nothing to lose.
  const report = (next: IWorkWeekChange) => {
    actions.setDraft(next)
    actions.setDirty(String([...next.workWeek].sort()) !== String([...value.workWeek].sort()) || next.cancelUserSettings)
    // A location has to work some day of the week, so an empty selection blocks Save instead.
    actions.setValid(next.workWeek.length > 0)
  }

  return (
    <Stack gap="sm" align="flex-start">
      <DaysOfWeekField
        value={days}
        firstDayOfWeek={firstDayOfWeek}
        dayNames={dayNames}
        ariaLabel={formatMessage({ id: 'locationGeneral.label.workWeek' })}
        onChange={(next) => {
          setDays(next)
          report({ workWeek: next, hoursPerDay: value.hoursPerDay, cancelUserSettings: applyToEveryone })
        }}
      />
      <ApplyToRow
        value={applyToEveryone}
        customCount={customCount}
        onChange={(next) => {
          setApplyToEveryone(next)
          report({ workWeek: days, hoursPerDay: value.hoursPerDay, cancelUserSettings: next })
        }}
      />
    </Stack>
  )
}

export const WorkdayEditor = ({ value, customCount, actions }: { value: IWorkWeekValue; customCount: number; actions: IEditorActions<IWorkWeekChange> }): React.ReactElement => {
  const [hours, setHours] = useState<number | null | undefined>(value.hoursPerDay)
  const [applyToEveryone, setApplyToEveryone] = useState(false)

  const report = (next: IWorkWeekChange) => {
    actions.setDraft(next)
    actions.setDirty((next.hoursPerDay ?? null) !== (value.hoursPerDay ?? null) || next.cancelUserSettings)
    // A cleared field is a change worth warning about on the way out, but not one Save may commit -
    // the same split the work-week days use. Range, not just type: antd clamps on blur rather than
    // on change, so 0 reaches here, and the payload treats 0 hours as "unset" and restores the
    // default without telling the user.
    actions.setValid(typeof next.hoursPerDay === 'number' && next.hoursPerDay >= HOURS_MIN && next.hoursPerDay <= HOURS_MAX)
  }

  return (
    <Stack gap="sm" align="flex-start">
      <Stack direction="horizontal" align="center" gap="xs" wrap>
        <TextField
          autoFocus
          ariaLabel="Workday hours"
          variant="number"
          step={HOURS_STEP}
          min={HOURS_MIN}
          max={HOURS_MAX}
          style={{ width: 80 }}
          value={hours ?? undefined}
          onChange={(next) => {
            // TextField's onChange is widened for every variant; in number mode anything that is
            // not a number means the field was cleared. Round to the nearest half hour, so 7.4
            // does not survive as 7.4.
            const rounded = typeof next === 'number' ? Math.round(next * 2) / 2 : null
            setHours(rounded)
            report({ workWeek: value.workWeek, hoursPerDay: rounded, cancelUserSettings: applyToEveryone })
          }}
        />
        <Text tone="secondary" size="sm">
          <IntlMessages id="app.hours" />
        </Text>
      </Stack>
      <Text tone="tertiary" size="sm">
        <IntlMessages id="locationGeneral.workdayHint" />
      </Text>
      <ApplyToRow
        value={applyToEveryone}
        customCount={customCount}
        onChange={(next) => {
          setApplyToEveryone(next)
          report({ workWeek: value.workWeek, hoursPerDay: hours, cancelUserSettings: next })
        }}
      />
    </Stack>
  )
}
